<template>
  <header>
    <!-- Desktop Account links -->
    <header v-if="isDesktopView"
            id="utdDeskBanner"
            class="wk-header utdDeskBanner"
            :class="{ 'navOverflowed': navOverflowed,
                      'hasShadow': shouldShowShadow
            }"
            role="banner">
      <div class="wk-header-container">
        <div class="utd-header-top-left">
          <navigation-skip-links />
          <utd-logo />
          <utd-content-searchbar v-if="searchBarHeader" />
        </div>
        <div class="utd-header-top-right"
             :class="{'login-register': renderHeaderNavButtons}"
             role="menubar">
          <ul class="wk-nav" role="menu" aria-label="header user">
            <li v-if="showResourceCenterGuide" role="menuitem">
              <utd-pendo-resource-center :class="{'loggedIn': isLoggedIn}" />
            </li>
            <li id="wkUtdWelcomeName"
                v-hover-delay="{ afterDelay: () => null,
                                 afterUnhoverDelay: hideAcctNav,
                                 sharedHoverState: 'accountNav' }"
                role="menuitem"
                class="wkUtdWelcomeName"
                :class="{'loggedIn': isLoggedIn}"
                :aria-expanded="isShowAcctNav"
                aria-haspopup="true"
                tabindex="0"
                @keyup.escape="toggleAcctNav"
                @keyup.enter="toggleAcctNav()">
              <a>
                <span>
                  <div v-hover-delay="{ afterDelay: showAcctNav,
                                        afterUnhoverDelay: () => null,
                                        sharedHoverState: 'accountNav' }"
                       class="chevron-down"
                       role="button"
                       :aria-label="welcomeName+' Account navigation'"
                       @click="toggleAcctNav()" />
                  <span v-hover-delay="{ afterDelay: showAcctNav,
                                         afterUnhoverDelay: () => null,
                                         sharedHoverState: 'accountNav' }"
                        class="welcomeLabel"
                        @click="toggleAcctNav()"
                        v-text="welcomeName" />
                </span>
              </a>

              <account-menu v-if="isDesktopView"
                            v-show="isShowAcctNav"
                            id="utdAcctNav"
                            class="utdAcctNav"
                            @hide-nav="hideAcctNav"
                            @click-into="hideAcctNav" />
            </li>
            <login-register-buttons v-if="showLoginRegisterButtons" />
            <li v-show="showTicker"
                role="menuitem"
                class="utd-user-cme"><!--
                --><a :id="cmeTickerId('Header')"
                      tabindex="0"
                      :href="dashboardLink"
                      @focus="hideAcctNav()"><!--
                      --><utd-cme-ticker /></a>
            </li>
            <li v-show="isLoggedIn"
                role="menuitem"
                class="utd-log-inout"><!--
                --><a href="/logout"
                      tabindex="0"
                      @click="resolveLogout('/logout')"
                      @focus="hideAcctNav() && hideContents()"
                      v-text="$t('LOGIN.SIGN_OUT')" />
            </li>
            <li role="menuitem" class="hamburguer-menu" :class="{ loggedIn: isLoggedIn }">
              <navigation-menu v-if="isHomepageRedesign2024" />
            </li>
          </ul>
          <ul role="menu" class="wk-nav utd-right">
            <li role="menuitem" title="Trial Subscribe"><trial-subscribe /></li>
          </ul>
        </div>
      </div>
    </header>
    <nav v-if="shouldShowSecondNavbar"
         id="utdNavRoot"
         class="wk-navbar utdNavRoot"
         role="navigation"
         :class="{'open': isNavOpen,
                  'topicNav': isShowTopicNav,
                  'navOverflowed': navOverflowed,
                  'isMobileWebHomepageSearch': isMobileWebSearchExpanded}">
      <!-- Desktop Navbar -->
      <div class="wk-navbar-container"
           :class="{ 'navbarFixed': isNavbarFixed }"
           role="menubar">
        <nav id="utdMainNav"
             v-hover-delay="{ afterDelay: showTopicNav,
                              afterUnhoverDelay: hideTopicNav,
                              sharedHoverState: 'utdMainNav' }"
             class="wk-navbar utdMainNav"
             role="navigation">
          <ul v-if="!isProspectView && !isHomepageRedesign2024"
              class="wk-nav"
              role="menu"
              aria-label="navigation">
            <li v-if="isNotDesktopView"
                id="homeHeader">
              <a href="/"
                 tabindex="0"
                 role="menuitem"
                 @focus="hideContents"
                 v-text="$t('HOME')" />
            </li>

            <li v-if="shouldShowToc"
                id="tocHeader"
                v-hover-delay="{ afterDelay: () => null,
                                 afterUnhoverDelay: hideContents,
                                 sharedHoverState: 'utdMainNav' }"
                class="tocHeader"
                :class="{'active': isShowContents}"
                @keypress.enter="toggleContents($event)">
              <a :href="localizeAnchor('/contents/', getTocBaseUrl, language)"
                 role="menuitem"
                 tabindex="0"
                 aria-haspopup="true"
                 :aria-expanded="isShowContents"
                 @click="toggleContents($event)">
                <span>
                  <span v-text="$t('HEADER.CONTENTS')" />
                </span>
              </a>
              <toc-home-menu v-if="isDesktopView" @dismiss-contents-menu="onContentMenuDismiss" />
            </li>

            <li v-if="shouldShowCalculators"
                id="calcHeader">
              <a :href="localizeAnchor('/contents/',
                                       'table-of-contents/calculators/categorized', language)"
                 role="menuitem"
                 tabindex="0"
                 @focus="hideContents"
                 v-text="$t('HEADER.CALCULATORS')" />
            </li>

            <li v-if="isUccState"
                id="pcuHeader">
              <a href="/contents/practice-changing-updates"
                 role="menuitem"
                 tabindex="0"
                 @focus="hideContents"
                 v-text="$t('HEADER.PRACTICE_CHANGING_UPDATES')" />
            </li>

            <li v-if="isUccState"
                id="whatsNewHeader">
              <a :href="localizeAnchor('/contents/','table-of-contents/whats-new', language)"
                 tabindex="0"
                 role="menuitem"
                 v-text="$t('HEADER.WHATS_NEW')" />
            </li>

            <li v-if="isUccState"
                id="patientEdHeader">
              <a :href="localizeAnchor('/contents/',
                                       'table-of-contents/patient-education', language)"
                 tabindex="0"
                 role="menuitem"
                 v-text="$t('HEADER.PATIENT_EDUCATION')" />
            </li>

            <li v-if="showDrugInteractionsApp && !isUccState"
                id="drugIntHeader">
              <a href="/drug-interactions?source=responsive_home"
                 tabindex="0"
                 role="menuitem"
                 v-text="$t('HEADER.DRUG_INTERACTIONS')" />
            </li>

            <li v-if="!isUccState"
                v-show="hasPathwaysAccess"
                id="pathwaysHeader">
              <a :href="localizeAnchor('/contents/',
                                       'table-of-contents/pathways', language)"
                 role="menuitem"
                 v-text="$t('SEARCH.UPTODATE_PATHWAYS')" />
            </li>

            <li v-if="!isUccState && isShowRxTransitions"
                id="rxTransitionsHeader">
              <a href="/rxtransitions?source=responsive_home"
                 role="menuitem"
                 v-text="$t('HEADER.RX_TRANSITIONS')" />
            </li>
          </ul>
          <account-menu v-if="isNotDesktopView && !isHomepageRedesign2024" />
          <home-account-menu v-if="isHomepageRedesign2024" />
        </nav>

        <!-- Mobile logged in user view -->
        <ul v-if="shouldShowMobileCme"
            id="utdMobileCme"
            class="wk-nav utd-left"
            role="menu">
          <li v-if="welcomeName"
              id="wkUtdWelcomeName"
              class="wkUtdWelcomeName"
              role="menuitem"
              v-text="welcomeName" />
          <li v-if="showTicker"
              id="wkutdCmeLi"
              class="wkutdCmeLi"
              role="menuitem">
            <a :id="cmeTickerId('Header')"
               :href="dashboardLink"
               class="headerCmeTicker"
               @click="hideMyAccountSidebar()">
              <utd-cme-ticker />
            </a>
          </li>
        </ul>

        <!-- Mobile user search results view -->
        <div v-if="shouldShowMobileSearchView" id="utdMobileBar" class="utdMobileBar">
          <button v-if="showBackButton"
                  v-utd-back
                  class="search-bar-left"
                  @click="navGoBack()" />
          <utd-content-searchbar ref="mobileSearchBar" />
        </div>

        <!-- Mobile authenticated -->
        <login-register-buttons v-if="renderHeaderNavButtons" id="loginRegisterButtons" />
        <ul class="wk-nav utd-right">
          <li role="presentation"><trial-subscribe /></li>
        </ul>
        <ul v-hover-delay="{ afterDelay: showTopicNav,
                             afterUnhoverDelay: hideTopicNav,
                             sharedHoverState: 'utdMainNav' }"
            class="wk-more-menu"
            tabindex="0"
            aria-label="Navigation menu"
            role="button"
            @keypress.enter="toggleNavMenu"
            @click="toggleNavMenu()">
          <li tabindex="0"
              aria-haspopup="true"
              aria-controls="scroll-topic-menu"
              :aria-expanded="isShowTopicNav"
              aria-label="Open navigation menu button"
              @focus="hideAcctNav"
              @keypress.enter="toggleNavMenu">
            <div v-if="isHomepageRedesign2024"
                 class="menu-icon"
                 :class="isNavOpen ? 'wkce-icon-filled-close' : 'wkce-icon-menu'" />
            <div v-if="!hideForOidcPartner &!isHomepageRedesign2024" class="utd-hamburger">
              <div class="bar one">&zwj;</div>
              <div class="bar two">&zwj;</div>
              <div class="bar three">&zwj;</div>
            </div>
            <label v-text="$t('HEADER.MENU')" />
          </li>
        </ul>
      </div>
    </nav>
    <prospect-banner v-if="renderHeaderMarketingBanner" />
    <div id="utdAppShield"
         :class="{'coverApp': isNavOpen}"
         @click="toggleNavMenu()" />
  </header>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { getWindow, getDocument, retainElementFocus, getDimensions } from '_acaSrc/utility/DOM';
import { safeTimeout } from '_acaSrc/utility/timers';
import { C_UI, C_CME_UI_LINKS, C_EVENTS } from '_acaSrc/utility/constants';
import { dispatchEvent } from '_acaSrc/utility/Events';

import UtdLogo from '_acaSrc/components/header/utdLogo/UtdLogo.vue';
import NavigationSkipLinks from '_acaSrc/components/header/NavigationSkipLinks.vue';
import UtdContentSearchbar from '_acaSrc/components/contents/search/SearchBar.vue';
import AccountMenu from '_acaSrc/components/header/AccountMenu.vue';
import UtdCmeTicker from '_acaSrc/components/header/UtdCmeTicker.vue';
import TrialSubscribe from '_acaSrc/components/header/TrialSubscribe.vue';
import LoginRegisterButtons from '_acaSrc/components/header/LoginRegisterButtons.vue';
import TocHomeMenu from '_acaSrc/components/header/TocHomeMenu.vue';
import NavigationMenu from '_acaSrc/components/header/NavigationMenu.vue';
import ProspectBanner from '_acaSrc/components/ui/ProspectBanner.vue';
import HomeAccountMenu from '_acaSrc/components/homePageRedesign/HomeAccountMenu.vue';
import HoverDelay from '_acaSrc/components/shared/utd/UtdHoverDelay.vue';
import utdBack from '_acaSrc/directives/UtdBack.directive';
import PubSub from '_acaSrc/utility/PubSub';
import { SET_IS_FIXED_TOOLBAR, SET_BODY_SCROLL_LOCK } from '_acaSrc/store/app.store';
import { SET_MOBILE_WEB_SEARCH_INTERACTION } from '_acaSrc/store/search.store';
import UtdPendoResourceCenter from '_acaSrc/components/shared/utd/UtdPendoResourceCenter.vue';

const FIXED_TOOLBAR_POSITION_LANDING = 20;

export default {
    components: {
        UtdPendoResourceCenter,
        UtdLogo,
        NavigationSkipLinks,
        UtdContentSearchbar,
        AccountMenu,
        UtdCmeTicker,
        TrialSubscribe,
        LoginRegisterButtons,
        NavigationMenu,
        TocHomeMenu,
        ProspectBanner,
        HomeAccountMenu
    },
    directives: {
        HoverDelay,
        utdBack
    },
    data() {
        return {
            toOverflow: null,
            navOverflowed: false,
            cancelOverflowTimeout: null,
            cancelResizeTimeout: null,
            startUrl: '',
            isShowTopicNav: false,
            isShowAcctNav: false,
            isNavOpen: false,
            isShowContents: false,
            dashboardLink: C_CME_UI_LINKS.dashboard,
            isNavbarFixed: false
        };
    },
    computed: {
        ...mapGetters('app', [
            'isUccState',
            'isProspectView',
            'hideForOidcPartner',
            'transitions',
            'routeLoading',
            'isSearchView',
            'isLandingHome',
            'isFixedToolbar',
            'isAnonymousUser',
            'router',
            'isProspectMode'
        ]),
        ...mapGetters('toc', [ 'isTocView' ]),
        ...mapGetters('user', [ 'language', 'userLoggedIn', 'isCustomer' ]),
        ...mapGetters('profile', [ 'welcomeName' ]),
        ...mapGetters('cme', [ 'showTicker', 'cmeTickerId' ]),
        ...mapGetters('topic', [ 'isTopicView' ]),
        ...mapGetters('graphic', [ 'isGraphicView' ]),
        ...mapGetters('search', [
            'searchBarHeader',
            'isSearchView',
            'isHomeSearchEmpty',
            'isMobileWebSearchExpanded'
        ]),
        ...mapGetters('device', [ 'isDesktopView', 'isNotDesktopView' ]),
        ...mapGetters('feature', [
            'showDrugInteractionsApp',
            'hasPathwaysAccess',
            'isShowRxTransitions',
            'topicRedesign',
            'isHomepageRedesign2024'
        ]),
        ...mapGetters('pendo', [ 'showResourceCenterGuide' ]),
        isLoggedIn() {
            return this.userLoggedIn;
        },
        renderHeaderNavButtons() {
            return (this.routeLoading === 'search' &&
                    this.isHomeSearchEmpty &&
                    !this.isMobileWebSearchExpanded) ||
                   (this.isDesktopView && !this.isLoggedIn);
        },
        getTocBaseUrl() {
            return this.topicRedesign && this.isNotDesktopView
                ? 'table-of-contents-home'
                : 'table-of-contents';
        },
        hasNavigated() {
            return this.startUrl !== getWindow().location.href;
        },
        showBackButton() {
            return !this.hideForOidcPartner || this.hasNavigated;
        },
        shouldShowMobileCme() {
            return (this.userLoggedIn)
                    && !this.searchBarHeader
                    && this.isNotDesktopView
                    && !this.isMobileWebSearchExpanded;
        },
        shouldShowMobileSearchView() {
            return (this.searchBarHeader || this.isMobileWebSearchExpanded) &&
                    this.isNotDesktopView;
        },
        shouldShowToc() {
            return this.isCustomer || this.userLoggedIn;
        },
        shouldShowCalculators() {
            return (this.isCustomer || this.userLoggedIn)
                    && !this.isUccState;
        },
        shouldShowSecondNavbar() {
            return this.isHomepageRedesign2024 ?
                (this.userLoggedIn || this.isAnonymousUser) && this.isNotDesktopView :
                true;
        },
        renderHeaderMarketingBanner() {
            return this.routeLoading === 'topic'
                    || this.routeLoading === 'topicLanguage';
        },
        shouldShowShadow(){
            return this.isFixedToolbar &&
            this.isHomepageRedesign2024 &&
            !this.isTopicView &&
            !this.isTocView &&
            !this.isSearchView &&
            !this.isGraphicView;
        },
        showLoginRegisterButtons() {
            return this.isAnonymousUser && this.isDesktopView;
        }
    },
    watch: {
        isMobileWebSearchExpanded(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    const searchBar = this.$refs.mobileSearchBar.$el;
                    const input = searchBar.querySelector('#tbSearch');
                    input && input.focus();
                    new PubSub().publish(C_EVENTS.POSITION_FOOTER, {
                        reset: true
                    });
                });
            }
        }
    },
    mounted() {
        this.startUrl = getWindow().location.href;
        this.setupListeners();
        this.setupTransitions();
    },
    beforeUnmount() {
        if (this.toOverflow) {
            this.cancelOverflowTimeout();
        }
        this.cancelResizeTimeout();
        this.clearResizeListener(this.overflowResizeListener);
        this.clearResizeListener(this.navMenuResizeListener);
        this.clearScrollListener(this.scrollListener);
        document.removeEventListener('click', this.handleClickEvent);
        new PubSub().unsubscribe('wkutd.repositionDialog', this.checkDesktopOverflows);
    },
    methods: {
        ...mapActions('login', [ 'resolveLogout' ]),
        ...mapMutations('app', [ 'SET_IS_FIXED_TOOLBAR', 'SET_BODY_SCROLL_LOCK' ]),
        ...mapMutations('search', [ 'SET_MOBILE_WEB_SEARCH_INTERACTION' ]),
        navGoBack() {
            this.isMobileWebSearchExpanded ? 
                this[SET_MOBILE_WEB_SEARCH_INTERACTION](false) :
                new PubSub().publish('wkutd.closeMyAccountSidebar');
        },
        setupListeners() {
            this.overflowResizeListener = this.setResizeListener(
                getWindow(), this.checkDesktopOverflows
            );
            this.navMenuResizeListener = this.setResizeListener(getWindow(), this.checkResizeScreen);
            this.scrollListener = this.setScrollListener(getWindow(), this.checkFixedToolbarState);
            this.homeScrollListener = this.setScrollListener(getWindow(), this.setFixedBarOnHome);
            document.addEventListener('click', this.handleClickEvent);
            this.cancelResizeTimeout = safeTimeout(() => {
                dispatchEvent('resize');
            }, C_UI.MODAL_CLOSING_DELAY_MS, { cancelHook: true }).cancelTimeout;
            new PubSub().subscribe('wkutd.repositionDialog', this.checkDesktopOverflows);
        },
        setFixedBarOnHome(){
            const isCmeRoute = this.router.current().name.indexOf('cme.') === 0;

            if (this.isHomepageRedesign2024 &&
              (isCmeRoute || this.isSearchView || this.isHomeSearchEmpty)){
                this.isNavbarFixed = getWindow().scrollY > 1;
                return;
            }
            this.isNavbarFixed = false;
        },
        setupTransitions() {
            this.transitions.onBefore({}, () => {
                this.isShowTopicNav = false;
                if (this.isNavOpen) {
                    this.closeNavMenu();
                }
                if (this.isShowAcctNav) {
                    this.hideAcctNav();
                }
            });
            this.transitions.onSuccess({ from: 'login' }, () => {
                // This resize event will force a redraw of the main nav bar for desktop after
                // login to ensure that the "RX Transitions" menu entry does not wrap at smaller
                // browser widths (< 950px). It is recommended to not remove the transition filter,
                // as that may introduce potentially hard to track down UI anomalies.
                dispatchEvent('resize');
            });
        },
        hideAcctNav() {
            this.isShowAcctNav = false;
        },
        showAcctNav() {
            this.isShowAcctNav = true;
            // hide other menus and/or modals
            this.isShowTopicNav = false;
            new PubSub().publish('wkutd.dismissAutoComplete');
            new PubSub().publish('wkutd.closeMyAccountSidebar');
        },
        toggleAcctNav() {
            if (!this.isShowAcctNav) {
                return this.showAcctNav();
            }
            this.hideAcctNav();
        },
        showContents() {
            if (!this.isDesktopView || this.isShowContents) {
                return;
            }
            this.isShowContents = true;
        },
        hideContents() {
            if (!this.isDesktopView || !this.isShowContents) {
                return;
            }
            this.isShowContents = false;
        },
        showTopicNav() {
            if (!this.isDesktopView) {
                return;
            }
            this.hideAcctNav();
            this.isShowTopicNav = true;
        },
        hideTopicNav() {
            if (!this.isDesktopView) {
                return;
            }
            this.isShowTopicNav = false;
        },
        toggleNavMenu() {
            if (this.isNotDesktopView) {
                this.isNavOpen = !this.isNavOpen;
                this[SET_BODY_SCROLL_LOCK](this.isNavOpen);
                if (this.isNavOpen) {
                    new PubSub().publish('wkutd.dismissAutoComplete');
                    new PubSub().publish('wkutd.closeMyAccountSidebar');
                }
            }
            else if (!this.isShowTopicNav) {
                this.showTopicNav();
                new PubSub().publish('wkutd.dismissAutoComplete');
                retainElementFocus('#tocHeader a');
            }
            else {
                this.hideTopicNav();
            }
        },
        checkNavOverflow() {
            // If user is not logged in, check if 'Register'/'Log In'
            // buttons would overflow the main navigation container
            if (!this.userLoggedIn) {
                // "Register" & "Login" buttons are considered overflowed if the left edge
                // of their container is less than the right edge of the width of the nav
                // bar container contents.
                const wkNavBar = getDimensions(
                    document.querySelector('.utdMainNav .wk-nav'));
                const wkNavUtdRgt = getDimensions(
                    document.querySelector('.utdNavRoot login-register-buttons'));
                if (wkNavUtdRgt && wkNavBar) {
                    this.navOverflowed = wkNavUtdRgt.left < wkNavBar.width;
                }
            }
            else {
                // If user is logged in, clear navOverflowed flag
                // as it is only relevant for unauthenticated Enterprise views
                this.navOverflowed = false;
            }
        },
        checkDesktopOverflows() {
            if (this.toOverflow !== null) {
                this.cancelOverflowTimeout();
            }
            const { promise, cancelTimeout } = safeTimeout(() => {
                // These tests are only applicable to desktop views
                if (this.isDesktopView) {
                    this.checkNavOverflow();
                }
                else {
                    // Ensure navOverflowed flag gets shut off
                    // when not in desktop mode.
                    this.navOverflowed = false;
                }
            }, C_UI.DESKTOP_OVERFLOW_DELAY_MS, { cancelHook: true });
            this.cancelOverflowTimeout = cancelTimeout;
            this.toOverflow = promise;
        },
        toggleContents(event) {
            if (this.isDesktopView) {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.isShowContents = !this.isShowContents;
                return false;
            }
        },
        // eslint-disable-next-line complexity
        checkFixedToolbarState() {
            if (this.rxTransitionsHamburger()) {
                this[SET_IS_FIXED_TOOLBAR](true);
                return;
            }

            if(!this.isHomepageRedesign2024
                && !this.isSearchView
                && !this.isTopicView
                && !this.isTocView
                && !this.isGraphicView
                && !this.isLandingHome) {
                if (this.isFixedToolbar) {
                    this[SET_IS_FIXED_TOOLBAR](false);
                }
                return;
            }

            const fixedOffsetPosition = this.isLandingHome
                ? FIXED_TOOLBAR_POSITION_LANDING : C_UI.FIXED_TOOLBAR_POSITION_PX;

            const isScrolledTop = getWindow().pageYOffset >= fixedOffsetPosition;
            if (this.isFixedToolbar !== isScrolledTop) {
                this[SET_IS_FIXED_TOOLBAR](isScrolledTop);
            }
        },
        rxTransitionsHamburger() {
            const screenWidth = getDocument().documentElement.clientWidth;
            if (this.isShowRxTransitions
             && this.hasPathwaysAccess
             && screenWidth < C_UI.RX_TRANSITIONS_MENU_BREAKPOINT) {
                return true;
            }
        },
        checkResizeScreen() {
            this.closeNavMenu();
            this.checkFixedToolbarState();
        },
        closeNavMenu() {
            if (this.isNavOpen) {
                this.isNavOpen = false;
                this[SET_BODY_SCROLL_LOCK](false);
            }
        },
        handleClickEvent(event) {
            if (this.isNotDesktopView) {
                this.handleMobileClickEvent(event);
            }
            return true;
        },
        handleMobileClickEvent(event) {
            const utdMainNav = document.getElementById('utdMainNav');
            const wkMoreMenu = document.querySelector(
                '#utdNavRoot .wk-navbar-container .wk-more-menu'
            );
            let doClose = false;
            if (!utdMainNav || !wkMoreMenu) {
                return;
            }

            // Close the nav menu if user has clicked outside of
            // the main navigation, and outside of the hamburger
            // menu activation trigger.
            // Or close the nav menu if user has clicked anywhere
            // inside of the nav menu
            if ((!utdMainNav.contains(event.target) && !wkMoreMenu.contains(event.target))
                    || utdMainNav.contains(event.target)) {
                doClose = true;
            }
            if (doClose) {
                this.closeNavMenu();
            }
        },
        onContentMenuDismiss() {
            this.isShowContents = false;
        },
        hideMyAccountSidebar() {
            new PubSub().publish('wkutd.closeMyAccountSidebar');
        }
    }
};
</script>
<style lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles.less';

.wk-nav li.wkUtdWelcomeName > a {
  .ds1-pl-0();
}

.utdWkHeader .wk-navbar .wk-more-menu .utd-hamburger {
  margin: 19px 10px;
}


.isMobile:not(.utdWkHomePage2024), 
.isTablet:not(.utdWkHomePage2024) {
  &.utdWkHeader .wk-navbar .wk-more-menu:focus,
  &.utdWkHeader .wk-navbar .wk-more-menu:focus-visible {
    .ds1-utd-element-outline-2();
    width: 38px;
    height: 52px;
    top: 2px;
    right: 2px;

    .utd-hamburger {
      margin: 17px 8px;
    }
  }
}

@media only screen and (max-width: 850px) {
  .isDesktop.utdWkHeader {
    &.fixedToolbar {
      .utd-header-top-right {
        .utd-user-cme,
        .utd-log-inout {
          display: none;
        }
      }
    }

    .utdWkHeader .wk-nav > li.utd-user-cme > a {
      .ds1-pt-0();
    }
  }
}

.btn-login-prospect {
  display: none;
  .ds1-pt-0(); /* stylelint-disable-line order/order */
}

.utdWkHomePage2024 {
  .wk-navbar {
    .wk-nav > li#wkutdCmeLi > a {
      background-color: transparent;
    }

    &.open {
      .wk-more-menu .utd-hamburger {
        width: 13px;
        height: 13px;
        margin: 21.5px 14.5px;

        .bar {
          background-color: @DS2-UTD-NAVBAR-ACTIVE-BG-COLOR;
        }

        .bar.one,
        .bar.three {
          transform-origin: 50% 7px;
        }

        .bar.one {
          transform: rotate(45deg) translateY(6px);
        }

        .bar.three {
          transform: rotate(-45deg) translateY(6px);
        }
      }

      .wk-navbar-container {
        background-color: @WKCE-BLUE-SHADE2;
      }
    }

    .wk-navbar-container .search-bar-left {
      color: @DS2-UTD-NAVBAR-BACK-LINK-TEXT-COLOR;
      font-size: 16px;
      line-height: 100%;
      width: auto;
      height: auto;
      top: 0;
      padding: 20px 14px;

      .automation_searchBarMobileBackBtn {
        display: none;
      }
    }

    .search-bar-ac-unit .search-bar-left {
      display: none;
    }

    .utdRoute-search& {
      .searchbar-container {
        padding: 12px 0;
      }
    }

    .newsearch-submit {
      display: inline-flex;
      width: 30px;
      background-color: @DS2-UTD-NAVABAR-SEARCH-SUBMIT-BG-COLOR;

      &::after {
        top: 9px;
        font-size: 14px;
      }
    }

    .search-clear {
      right: 28px;
    }

    .wk-more-menu {
      &:focus {
        .ds1-utd-element-outline-2();
        padding: 14px;
        max-height: 52px;
        top: 2px;
        right: 2px;
      }

      >li {
        height: 24px;
      }
    }
  
  }

  &:not(.isHomeSearchEmpty ) .wk-navbar,
  &.isHomeSearchEmpty .isMobileWebHomepageSearch {
    .wk-navbar-container {
      background-color: @DS2-UTD-NAVBAR-BG-COLOR;
    }

    .search-bar-container {
      right: 56px;
      left: 44px;
    }
  }

  &.isHomeSearchEmpty  .wk-navbar:not(.isMobileWebHomepageSearch) .wk-more-menu:focus {
    outline-color: @WKCE-WHITE;
  }

  &.isHomeSearchEmpty .wk-navbar.isMobileWebHomepageSearch {
    .wk-more-menu {
      color: @DS2-UTD-NAVBAR-BARS-BG-COLOR;
    }

    .wk-navbar-container {
      &.navbarFixed {
        background-color: @DS2-UTD-NAVBAR-BG-COLOR;
      }

      .utdMobileBar .search-bar-container .search-bar .utdBarSearchCtrlContainer .utdBarSearchCtrl {
        .ds1-pl-2();
        padding-right: 58px;
        font-size: 12px;
        font-weight: 400;
        border: 1px solid @DS2-UTD-HEADER-SEARCH-MOBILE-INPUT-BORDER-COLOR;
  
        &::placeholder {
          color: @DS2-UTD-SEARCH-INPUT-PLACHOLDER-COLOR;
        }
  
        &:focus {
          outline: @DS1-UTD-FOCUS-OUTLINE;
        }
      }
    }

    &.open .wk-more-menu {
      color: @DS2-UTD-NAVBAR-CLOSE-BTN-TEXT-COLOR;
    }
  }

  .wk-navbar.open .wk-more-menu .utd-hamburger { /* stylelint-disable-line no-duplicate-selectors */
    margin: 21.5px 14px;
    width: 13px;
    height: 13px;
  }

  .utdWkHeader .search-bar-container {
    left: 48px;
  }

  :not(.isHomeSearchEmpty)& #tbSearch {
    .ds1-pl-2();
    padding-right: 58px;
    font-size: 12px;
    border: 1px solid @DS2-UTD-HEADER-SEARCH-MOBILE-INPUT-BORDER-COLOR;

    &::placeholder {
      font-weight: 300;
      color: @DS2-UTD-SEARCH-INPUT-PLACHOLDER-COLOR;
    }

    &:focus {
      outline: 2px solid @WKCE-BLACK;
    }
  }

  #wkUtdWelcomeName:hover > a {
    color: @DS2-UTD-NAVBAR-LOGIN-BG-COLOR;
  }

  &[class*="utdRoute-cme"] .wk-header,
  &.utdWkTopic .wk-header {
     border-bottom: @DS1-UTD-GRAY-SINGLE-BORDER;
  }
}

.utdWkHomePage2024 .wk-navbar-container {
  transition: all 0.3s ease-in-out;
  background-color: transparent;

  &.navbarFixed {
    box-shadow: @DS1-BOX-SHADOW-RAISED;

    .isHomeSearchEmpty& {
      background-color: @WKCE-BLUE-SHADE2;
    }
  }
}

.navBarHamburgerMenu {
  .isDesktop.utdWkHeader {
    &.fixedToolbar {
      #appContainer {
        > header {
          > header {
            position: fixed;
            display: block;
            width: auto;
            top: 0;
            left: 0;
            right: 86px;
            height: @WK-HEADER-CONTAINER-HEIGHT;

            .search-bar-ac-unit {
              right: 0;

              .utd-logo-cc& {
                right: 5em;
              }
            }

           .utdWkHomePage2024& {
            right: 0;
           }

            .search-bar-container {
              top: 0;
            }
          }

          > .wk-navbar {
            position: fixed;
            top: 0;

            &.topicNav {
              #utdMainNav {
                display: block;
                background-color: @WKCE-BLUE-SHADE1;
              }

              #utdMainNav .wk-nav > .tocHeader.active > a {
                background-color: @WKCE-BLUE;
              }

              .wk-more-menu {
                background-color: @WKCE-BLUE-SHADE1;

                .utdWkHomePage2024& {
                    background-color: transparent;
                }
              }
            }

            #utdMainNav {
              display: none;
              background-color: @WKCE-BLUE;
              position: fixed;
              min-width: 200px;
              top: @WK-HEADER-CONTAINER-HEIGHT;
              bottom: auto;
              right: 0;
              left: auto; /* stylelint-disable-line declaration-block-no-redundant-longhand-properties */

              .wk-nav {
                display: block;
                margin: 0;
                padding: 8px 0;
                float: none;

                & > li {
                  display: block;
                  position: relative;
                  border-bottom: 0;
                  padding: 0;
                  margin: 0;
                  float: none;

                  & > a {
                    display: block;
                    color: @WKCE-WHITE;
                    background-color: @WKCE-BLUE-SHADE1;
                    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
                    line-height: 16px;
                    padding: 8px 24px;
                  }
                }
              }
            }

            .wk-more-menu {
              cursor: pointer;
              display: block;
              height: @WK-HEADER-CONTAINER-HEIGHT;
              padding: 0 16px;

              .utd-hamburger {
                display: inline-block;
                width: 12px;
                height: 13px;
                margin: 0;

                .bar {
                  height: 1px;

                  &.two {
                    .css-transform(scaleX(1) translateY(5px));
                    .css-transform-origin(1px 5px);
                  }

                  &.three {
                    .css-transform(translateY(10px));
                  }
                }
              }

              label {
                cursor: pointer;
                display: inline-block;
                font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
                font-size: 15px;
                line-height: @WK-HEADER-CONTAINER-HEIGHT;
                padding-left: 4px;
                color: @WKCE-WHITE;
                width: 38px;
              }
            }
          }
        }
      }
    }
  }
}

.isDesktop.utdWkHomePage2024 {
  &.utdWkHeader .wk-header .wk-header-container {
    :not(.navigation-menu-container) {
      .wk-nav > li:not(.login-btn, .register-btn) > a {
        color: @WKCE-GRAY ;
      }
    }

    .utd-header-top-right .navigation-menu .wk-nav {
      .ds1-pr-0();
      > li > a {
        color: @WKCE-BLUE-SHADE1 ;
      }
    }

    #wkUtdWelcomeName > a >span .chevron-down::before {
      background-image: url(data:image/svg+xml;base64,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);
      top: 2px;
    }

    #wkUtdWelcomeName > a > span {
      padding-left: 22px;
      flex-direction: row-reverse;
      flex-wrap: nowrap; /* stylelint-disable-line declaration-block-no-redundant-longhand-properties */
    }

    #wkUtdWelcomeName.loggedIn > a .welcomeLabel::before {
      color: @WKCE-BLUE-SHADE1;
    }

    #wkUtdWelcomeName&:hover {
      color: @WKCE-BLUE;
    }
  }

  &:not(.isHomeSearchEmpty) .wk-navbar .wk-navbar-container {
    border-bottom: none;
  }
}

.utdWkHomePage2024.utd-logo-cc {
  #wkUtdWelcomeName {
    margin-top: 18px;
  }
}

@media only screen and (min-width: 768px) {
  .navBarHamburgerMenu();

  .isDesktop.utdWkHomePage2024 {
      .hasShadow, &.fixedToolbar.utdRoute-AuthorsAndEditorsSpecialty .wk-header {
        box-shadow: @DS1-BOX-SHADOW-RAISED;
      }

      .utd-log-inout :hover {
        color: @WKCE-BLUE-SHADE1;
        background-color: @WKCE-BLUE-TINT5;
      }

      .utd-header-top-right .navigation-menu-nav .wk-nav li {
        .ds1-pv-0();
      }

      .wk-utd-cme {
        .ds1-ph-2();
        .ds1-pv-1();
        color: @DS1-UTD-GRAY-TEXT-COLOR;
        background-color: @WKCE-GRAY-TINT4;
        border-width: 0;

        &:hover {
          color: @WKCE-BLUE-SHADE1;
          background-color: @WKCE-BLUE-TINT5;
        }
      }
      .welcomeLabel::before {
        color: @WKCE-BLUE-SHADE1;
      }
      .wkce-icon-question {
        color: @WKCE-BLUE-SHADE1;
      }
      .btn-login-prospect {
        display: block;
      }

      .newsearch-submit {
        background: @WKCE-GRAY-TINT1;

        &:hover {
          opacity: 1;
          background-color: @DS2-UTD-NAVBAR-SEARCH-SUBMIT-BG-COLOR;
        }
      }

      .utdWkHeader .wk-navbar-container #tbSearch::placeholder {
        font-weight: 300;
      }

      &.anonymous-user{
        &.utdWkHeader .wk-header .wk-header-container {
          .utd-header-top-right {
            .wk-nav {
                #wkUtdWelcomeName {
                .ds1-ph-1();

                >a {
                  .ds1-pr-0();
                }
              }
            }

            .navigation-menu-nav .wk-nav {
              .ds1-pr-0();
            }
          }

          .wk-nav > li > a:not(#wkUtdWelcomeName) {
            .ds1-pr-2();
          }
        }
      }

      &.isHomeSearchEmpty , &:not(.isHomeSearchEmpty) {
        .wk-navbar-container,
        .wk-navbar {
          background-color: @WKCE-GRAY-TINT1;
          height: 48px;
        }

        #subscribeInterrupt .wk-navbar {
          background-color: unset;
          border-bottom: unset;

          .wk-nav > li > a{
            color: @WKCE-WHITE;
          }
        }
      }

      :not(.isHomeSearchEmpty)& #tbSearch {
        .ds1-pr-3();
        border: 1px solid @DS2-UTD-HEADER-SEARCH-INPUT-BORDER-COLOR;
      }
  }

  .isDesktop {
    .utd-header-top-right>.wk-nav {
        .ds1-mt-0();
    
        >li:not(.hamburguer-menu) {
          padding: 9px 0 8px;
        }
      }
  }

  .isDesktop.utdWkHomePage2024.anonymous-user .utd-header-top-right .welcomeLabel {
    display: none;
  }

  .isDesktop.utdWkHomePage2024.utd-logo-cc {
    #wkUtdWelcomeName {
      .ds1-mt-0();
    }
  }
}

@media only screen and (min-width: 812px) {
  .isDesktop.utdWkHomePage2024.isHomeSearchEmpty.anonymous-user {
    &.utdWkHeader .wk-header .wk-header-container {
      .utd-header-top-right {
        .wk-nav {
          #wkUtdWelcomeName {
            .ds1-pr-1();

            > a {
              .ds1-pr-2();
            }
          }
        }
      }
    }
  }
}

@media only screen and (min-width: 992px) {
  .isDesktop.utdWkHomePage2024.isHomeSearchEmpty .utd-header-top-right .welcomeLabel{
    display: block;
  }
}


@media only screen and (min-width: 1126px) {
  .isDesktop.utdWkHomePage2024.anonymous-user:not(.isHomeSearchEmpty) {
    &.utdWkHeader .wk-header .wk-header-container {
      .utd-header-top-right {
        .wk-nav {
          #wkUtdWelcomeName {
            .ds1-pr-2();

            >a {
              .ds1-pr-2();
            }
          }
        }
      }
    }
  }
}

@media only screen and (min-width: 1280px) {
  .isDesktop.utdWkHomePage2024 {
    &.anonymous-user .utd-header-top-right:not(.login-register) .welcomeLabel {
      display: block;
    }

    &.anonymous-user.isHomeSearchEmpty .utd-header-top-right.login-register .welcomeLabel{
      display: block;
    }

    .utdWkHeader& .wk-header>.wk-header-container .wk-nav #wkUtdWelcomeName.loggedIn>a .welcomeLabel {
      width: 100%;
      display: flex;
      line-height: normal;
      overflow: hidden;
      padding-left: 4px;

      &::before {
        top: 0;
      }
    }
  }
}

@media only screen and (min-width: 1400px) {
  .isDesktop.utdWkHomePage2024 {
    &.anonymous-user .utd-header-top-right.login-register .welcomeLabel {
      display: block;
    }
  }
}
</style>
